// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import validateEmail from "@insite/client-framework/Common/Utilities/validateEmail";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import validatePassword from "@insite/client-framework/Store/CommonHandlers/ValidatePassword";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import signIn from "@insite/client-framework/Store/Context/Handlers/SignIn";
import addAccount from "@insite/client-framework/Store/Data/Accounts/Handlers/AddAccount";
import { getCreateAccountReturnUrl } from "@insite/client-framework/Store/Pages/CreateAccount/CreateAccountSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox from "@insite/mobius/Checkbox";
import CheckboxGroup from "@insite/mobius/CheckboxGroup";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconPresentationProps } from "@insite/mobius/Icon";
import Eye from "@insite/mobius/Icons/Eye";
import EyeOff from "@insite/mobius/Icons/EyeOff";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";

import siteMessage from "@insite/client-framework/SiteMessage";
// import { TurnerAddAccountParameter } from "@turnerBlueprint/App/Handlers/TurnerAddAccount";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import validateReCaptcha from "@insite/client-framework/Store/Components/ReCaptcha/Handlers/ValidateReCaptcha";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import loadCurrentCountries from "@insite/client-framework/Store/Data/Countries/Handlers/LoadCurrentCountries";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { CountryModel, StateModel } from "@insite/client-framework/Types/ApiModels";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import ReCaptcha from "@insite/content-library/Components/ReCaptcha";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Select, { SelectProps } from "@insite/mobius/Select";
import { TippyPopupStyle } from "@turnerBlueprint/App/ComponentExtensions/Tippy/TippyPopupExtensions";
import PasswordRequirementsValidator from "@turnerBlueprint/App/Components/Common/PasswordRequirements";
import Notify from "@turnerBlueprint/App/Components/Notify";
import TippyPopup from "@turnerBlueprint/App/Components/Tippy/TippyPopup";
import { TurnerAddAccountParameter } from "@turnerBlueprint/App/Handlers/TurnerUpdateAccount";
import { xGaState } from "@turnerBlueprint/App/Store/Reducers/xGA";
import { xValidateOnSubmit } from "@turnerBlueprint/XDL/Utilities";
import { getDataLayer } from "@turnerBlueprint/XDL/Utilities/DataLayer";
import React, { FC, RefObject } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

// XNG-Changes
interface OwnProps extends WidgetProps {
    fields: {
        termsConditions: LinkFieldValue;
    };
}

const mapDispatchToProps = {
    addAccount,
    signIn,
    validatePassword,
    loadCurrentCountries,
    validateReCaptcha: makeHandlerChainAwaitable<{ location: string; recaptchaRef: RefObject<ReCAPTCHA> }, boolean>(
        validateReCaptcha,
    ),
};

const mapStateToProps = (state: ApplicationState) => ({
    accountSettings: getSettingsCollection(state).accountSettings,
    returnUrl: getCreateAccountReturnUrl(state),
    countries: getCurrentCountries(state),
});

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CreateAccountStyles {
    spinner?: LoadingSpinnerProps;
    centeringWrapper?: InjectableCss;
    mainGridContainer?: GridContainerProps;
    userGridItem?: GridItemProps;
    userGridContainer?: GridContainerProps;
    emailGridItem?: GridItemProps;
    emailTextField?: TextFieldPresentationProps;
    passwordGridItem?: GridItemProps;
    passwordTextField?: TextFieldPresentationProps;
    passwordIcon?: IconPresentationProps;
    confirmPasswordGridItem?: GridItemProps;
    confirmPasswordTextField?: TextFieldPresentationProps;
    confirmPasswordIcon?: IconPresentationProps;
    subscriptionGridItem?: GridItemProps;
    submitErrorGridItem?: GridItemProps;
    submitErrorTitle?: TypographyProps;
    submitGridItem?: GridItemProps;
    reCaptchaGridItem?: GridItemProps;
    submitButton?: ButtonPresentationProps;
    requirementsGridItem?: GridItemProps;
    requirementsTitle?: TypographyProps;
    passwordRequirementsGridItem?: GridItemProps;
    passwordRequirementsGridContainer?: GridContainerProps;
    passwordGridContainer?: GridContainerProps;
    halfWidthItem?: GridItemProps;
    // xng-changes
    separator?: GridItemProps;
    quarterWidthItem?: GridItemProps;
    fakeItem?: GridItemProps;
    link?: LinkPresentationProps;
    tippyPopup?: TippyPopupStyle;
}

export const createAccountStyles: CreateAccountStyles = {
    centeringWrapper: {
        css: css`
            display: flex;
            height: 300px;
            justify-content: center;
            align-items: center;
        `,
    },
    userGridItem: {
        width: [12, 12, 12, 12, 12],
        css: css`
            flex-direction: column;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            padding-left: 5px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    userGridContainer: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) => breakpointMediaQueries(theme, [null, null, css``], "min")}
        `,
    },
    emailGridItem: {
        width: 12,
    },
    passwordGridItem: {
        width: 12,
    },
    confirmPasswordGridItem: {
        width: 12,
    },
    subscriptionGridItem: {
        width: 12,
    },
    submitErrorGridItem: {
        width: 12,
    },
    submitErrorTitle: {
        color: "danger",
    },
    reCaptchaGridItem: { width: 12 },
    submitGridItem: {
        width: 12,
        css: css`
            justify-content: flex-end;
        `,
    },
    requirementsGridItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            flex-direction: column;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            padding-left: 50px;
                        `,
                    ],
                    "min",
                )}/* stylelint-disable-line declaration-block-single-line-max-declarations */
        `,
    },
    passwordRequirementsGridItem: {
        width: 12,
    },
    requirementsTitle: {
        variant: "h4",
        as: "h2",
    },
    halfWidthItem: {
        width: 12,
    },
};

const styles = createAccountStyles;
const firstNameRequiredFieldMessage = "First name is required";
const lastNameRequiredFieldMessage = "Last name is required";
const phoneNumberRequiredFieldMessage = "Phone Number is required";
const countryRequiredFieldMessage = "Country is required";
const streetAddressRequiredFieldMessage = "Street Address is required";
const cityRequiredFieldMessage = "City is required";
const stateRequiredFieldMessage = "State is required";
const postalCodeRequiredFieldMessage = "Zip code is required";

const formId = "createAccountForm";

const CreateAccount: FC<Props> = ({
    addAccount,
    signIn,
    validatePassword,
    loadCurrentCountries,
    validateReCaptcha,
    accountSettings,
    returnUrl,
    countries,
    fields,
}) => {
    React.useEffect(() => {
        if (!countries) {
            loadCurrentCountries();
        }
    });
    const emailRequiredFieldMessage = siteMessage("CreateNewAccountInfo_EmailAddress_Required");
    const passwordRequiredFieldMessage = siteMessage("CreateNewAccountInfo_Password_Required");
    const emailFieldMessage = siteMessage("CreateNewAccountInfo_EmailAddress_ValidEmail");
    const [firstName, setFirstName] = React.useState("");
    const [lastName, setLastName] = React.useState("");
    const [companyName, setCompanyName] = React.useState("");
    const [jobTitle, setJobTitle] = React.useState("");
    const [phoneNumber, setPhoneNumber] = React.useState("");
    const [country, setCountry] = React.useState("");
    const [streetAddress1, setStreetAddress1] = React.useState("");
    const [streetAddress2, setStreetAddress2] = React.useState("");
    const [city, setCity] = React.useState("");
    const [state, setState] = React.useState("");
    const [postalCode, setPostalCode] = React.useState("");
    const [email, setEmail] = React.useState("");

    const [stateObj, setStateObj] = React.useState<StateModel | undefined>(undefined);
    const [countryObj, setCountryObj] = React.useState<CountryModel | undefined>(undefined);

    const [firstNameError, setFirstNameError] = React.useState(firstNameRequiredFieldMessage);
    const [lastNameError, setLastNameError] = React.useState(lastNameRequiredFieldMessage);
    const [phoneNumberError, setPhoneNumberError] = React.useState(phoneNumberRequiredFieldMessage);
    const [countryError, setCountryError] = React.useState(countryRequiredFieldMessage);
    const [streetAddress1Error, setStreetAddress1Error] = React.useState(streetAddressRequiredFieldMessage);
    const [cityError, setCityError] = React.useState(cityRequiredFieldMessage);
    const [stateError, setStateError] = React.useState(stateRequiredFieldMessage);
    const [postalCodeError, setPostalCodeError] = React.useState(postalCodeRequiredFieldMessage);
    const [errorMessage, setErrorMessage] = React.useState("");

    const [password, setPassword] = React.useState("");
    // XNG-Changes
    const [passwordErrorMessage, setPasswordErrorMessage] =
        React.useState<React.ReactNode>(passwordRequiredFieldMessage);
    const [confirmPassword, setConfirmPassword] = React.useState("");
    const [confirmPasswordErrorMessage, setConfirmPasswordErrorMessage] = React.useState<React.ReactNode>("");
    const [showPassword, setShowPassword] = React.useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);
    const [isSubscribed, setIsSubscribed] = React.useState(true);
    const [agreeToTerms, setAgreeToTerms] = React.useState(false);
    const [isSubmitted, setIsSubmitted] = React.useState(false);
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [emailError, setEmailError] = React.useState("");
    const toasterContext = React.useContext(ToasterContext);
    const recaptchaRef: RefObject<ReCAPTCHA> = React.createRef();

    // XNG-Changes
    const termsConditionsUrl = useGetLink(fields.termsConditions).url || "";

    // XNG-Changes
    const shouldTrackRegistration = useAppSelector(state => {
        return (state as xGaState).xGA.registrationEvent;
    });
    const {
        passwordMinimumLength,
        passwordRequiresUppercase,
        passwordRequiresSpecialCharacter,
        passwordRequiresLowercase,
        passwordRequiresDigit,
    } = accountSettings;

    // const validateFirstNameField = (name: string) => {
    //     const errorMessage = !name ? firstNameRequiredFieldMessage : "";
    //     setFirstNameError(errorMessage);
    //     return !errorMessage;
    // };

    // const validateLastNameField = (name: string) => {
    //     const errorMessage = !name ? lastNameRequiredFieldMessage : "";
    //     setLastNameError(errorMessage);
    //     return !errorMessage;
    // };

    const phoneRegex = /^([\(\)/\-\.\+\s]*\d\s?(ext)?[\(\)/\-\.\+\s]*){10,}$/;

    const validatePhoneNumber = (name: string) => {
        const errorMessage = !name
            ? siteMessage("ContactUsForm_PhoneNumberIsRequiredErrorMessage")
            : phoneRegex.test(name)
            ? ""
            : siteMessage("ContactUsForm_PhoneNumberIsInvalidErrorMessage");
        setPhoneNumberError(errorMessage as string);
        return !errorMessage;
    };

    const validateCountry = (name: string) => {
        const errorMessage = !name ? countryRequiredFieldMessage : "";
        return true;
    };

    const validateStreetAddress1 = (name: string) => {
        const errorMessage = !name ? streetAddressRequiredFieldMessage : "";
        setStreetAddress1Error(errorMessage);
        return !errorMessage;
    };

    const validateCity = (name: string) => {
        const errorMessage = !name ? cityRequiredFieldMessage : "";
        setCityError(errorMessage);
        return !errorMessage;
    };

    const validateState = (name: string) => {
        const errorMessage = !name ? stateRequiredFieldMessage : "";
        setStateError(errorMessage);
        return !errorMessage;
    };

    const validatePostalCode = (name: string) => {
        const errorMessage = !name ? postalCodeRequiredFieldMessage : "";
        setPostalCodeError(errorMessage);
        return !errorMessage;
    };

    const validateConfirmPassword = (newPassword: string, newConfirmPassword: string) => {
        const errorMessage =
            newPassword !== newConfirmPassword
                ? siteMessage("CreateNewAccountInfo_PasswordCombination_DoesNotMatch")
                : "";
        setConfirmPasswordErrorMessage(errorMessage);
        return !errorMessage;
    };

    // XNG-Changes
    const validateSubmitEnabled = () => {
        return !(
            !email ||
            !firstName ||
            !lastName ||
            !phoneNumber ||
            state === "" ||
            !streetAddress1 ||
            !city ||
            !postalCode ||
            !password ||
            !confirmPassword ||
            !agreeToTerms ||
            confirmPassword !== password
        );
    };

    const emailChangeHandler = (email: string) => {
        // Xng-Change
        // const errorMessage = !email ? emailRequiredFieldMessage : validateEmail(email) ? "" : emailFieldMessage;
        // setEmailError(errorMessage);
        setEmail(email);
    };

    const firstNameChangeHandler = (name: string) => {
        setFirstName(name);
    };

    const lastNameChangeHandler = (name: string) => {
        setLastName(name);
    };

    const companyNameChangeHandler = (name: string) => {
        setCompanyName(name);
    };

    const jobTitleChangeHandler = (name: string) => {
        setJobTitle(name);
    };

    const phoneNumberChangeHandler = (name: string) => {
        setPhoneNumber(name);
    };

    const stateChangeHandler: SelectProps["onChange"] = (event: any) => {
        const selectedState = countries!
            .find(c => c.abbreviation === "US")
            ?.states!.find(state => state.id.toString() === event.target.value);
        validateState(selectedState?.abbreviation || "");
        setState(selectedState?.abbreviation || "");
        setStateObj(selectedState);
    };

    const countryChangeHandler: SelectProps["onChange"] = (event: any) => {
        const selectedCountry = countries!.find(country => country.id.toString() === event.target.value);

        validateCountry(selectedCountry?.abbreviation || "");
        setCountry(selectedCountry?.abbreviation || "");
        setCountryObj(selectedCountry);
    };

    const streetAddress1ChangeHandler = (name: string) => {
        validateStreetAddress1(name);
        setStreetAddress1(name);
    };

    const streetAddress2ChangeHandler = (name: string) => {
        setStreetAddress2(name);
    };

    const cityChangeHandler = (name: string) => {
        validateCity(name);
        setCity(name);
    };

    const postalCodeChangeHandler = (name: string) => {
        validatePostalCode(name);
        setPostalCode(name);
    };

    const passwordChangeHandler = (newPassword: string) => {
        validateConfirmPassword(newPassword, confirmPassword);
        validatePassword({
            password: newPassword,
            onComplete: errorMessage => {
                setPasswordErrorMessage(errorMessage);
            },
        });
        setPassword(newPassword);
    };

    const confirmPasswordChangeHandler = (newConfirmPassword: string) => {
        setConfirmPassword(newConfirmPassword);
        validateConfirmPassword(password, newConfirmPassword);
    };

    const onSuccessHandler = () => {
        setErrorMessage("");
        toasterContext.addToast({ body: translate("Account created successfully!"), messageType: "success" });
        // XNG-Changes
        if (shouldTrackRegistration) {
            const dataLayer = getDataLayer();

            if (!dataLayer) {
                return;
            }

            const googleAnalyticsData = {
                event: "registration",
                affiliation: companyName,
                jobtitle: jobTitle,
            };

            dataLayer.push(googleAnalyticsData);
        }

        setTimeout(() => {
            signIn({
                userName: email,
                password,
                rememberMe: false,
                returnUrl,
            });
        }, 500);
        window.location.href = "/";
    };

    const submitHandler = async (event: React.MouseEvent<HTMLButtonElement>) => {
        if (!isSubmitted) {
            setIsSubmitted(true);
        }

        if (!validateSubmitEnabled()) {
            return;
        }
        const errors = xValidateOnSubmit(formId);
        if (errors.length > 0) {
            return;
        }
        const isReCaptchaValid = await validateReCaptcha({ location: "CreateAccount", recaptchaRef });

        if (!isReCaptchaValid) {
            return;
        }

        setErrorMessage("");

        const accountInfo = {
            email,
            firstName,
            lastName,
            companyName,
            jobTitle,
            phoneNumber,
            country,
            streetAddress1,
            streetAddress2,
            city,
            state,
            postalCode,
            uri: "",
            properties: {},
        };

        const turnerAccountParam: TurnerAddAccountParameter = {
            email,
            userName: "",
            password,
            isSubscribed,
            onSuccess: onSuccessHandler,
            onError: error => {
                setErrorMessage(error);
                setIsSubmitting(false);
            },
            accountInfo,
        };

        addAccount(turnerAccountParam);
    };

    const isSubmitEnabled = validateSubmitEnabled();

    // xng-changes: restructure and adding placeholders
    return (
        <GridContainer {...styles.mainGridContainer}>
            <GridItem {...styles.userGridItem}>
                <GridContainer {...styles.userGridContainer}>
                    <GridItem {...styles.halfWidthItem}>
                        <TextField
                            {...styles.emailTextField}
                            label={translate("First Name")}
                            placeholder={translate("First Name")}
                            onChange={e => firstNameChangeHandler(e.currentTarget.value)}
                            value={firstName}
                            data-test-selector="createAccount_firstName"
                            required
                            // error={isSubmitted && firstNameError}
                            type="firstName"
                            {...{ formId }}
                        />
                    </GridItem>
                    <GridItem {...styles.halfWidthItem}>
                        <TextField
                            {...styles.emailTextField}
                            label={translate("Last Name")}
                            placeholder={translate("Last Name")}
                            onChange={e => lastNameChangeHandler(e.currentTarget.value)}
                            value={lastName}
                            data-test-selector="createAccount_lastName"
                            required
                            // error={isSubmitted && lastNameError}
                            type="lastName"
                            {...{ formId }}
                        />
                    </GridItem>
                    <GridItem {...styles.halfWidthItem}>
                        <TextField
                            {...styles.emailTextField}
                            label={translate("Company Name")}
                            placeholder={translate("Company Name")}
                            onChange={e => companyNameChangeHandler(e.currentTarget.value)}
                            value={companyName}
                            data-test-selector="createAccount_companyName"
                            {...{ formId }}
                            required={false}
                        />
                    </GridItem>
                    <GridItem {...styles.halfWidthItem}>
                        <TextField
                            {...styles.emailTextField}
                            label={translate("Job Title")}
                            placeholder={translate("Job Title")}
                            onChange={e => jobTitleChangeHandler(e.currentTarget.value)}
                            value={jobTitle}
                            data-test-selector="createAccount_jobTitle"
                            {...{ formId }}
                            required={false}
                        />
                    </GridItem>
                    <GridItem {...styles.halfWidthItem}>
                        <TextField
                            {...styles.emailTextField}
                            label={translate("Phone Number")}
                            placeholder={translate("(___) ___-___")}
                            onChange={e => phoneNumberChangeHandler(e.currentTarget.value)}
                            value={phoneNumber}
                            data-test-selector="createAccount_phoneNumber"
                            required
                            // error={isSubmitted && phoneNumberError}
                            type="phone"
                            {...{ formId }}
                        />
                    </GridItem>
                    <GridItem {...styles.emailGridItem}>
                        <StyledWrapper {...styles.separator}></StyledWrapper>
                    </GridItem>
                    {countries !== undefined && (
                        <GridItem {...styles.halfWidthItem}>
                            <Select
                                label={translate("Select Country")}
                                required
                                placeholder="Select"
                                onChange={countryChangeHandler}
                                value={countryObj !== undefined ? countryObj!.id : countries[0].id}
                                {...{ formId }}
                            >
                                <option value="">{translate("Select Country")}</option>
                                {countries!.map(cu => (
                                    <option key={cu.id} value={cu.id}>
                                        {cu.name}
                                    </option>
                                ))}
                            </Select>
                        </GridItem>
                    )}
                    <GridItem {...styles.emailGridItem}>
                        <TextField
                            {...styles.emailTextField}
                            label={translate("Address Line 1")}
                            placeholder={translate("Address")}
                            onChange={e => streetAddress1ChangeHandler(e.currentTarget.value)}
                            value={streetAddress1}
                            data-test-selector="createAccount_streetAddress"
                            required
                            // error={isSubmitted && streetAddress1Error}
                            type="address"
                            {...{ formId }}
                        />
                    </GridItem>
                    <GridItem {...styles.emailGridItem}>
                        <TextField
                            {...styles.emailTextField}
                            label={translate("Address Line 2")}
                            placeholder={translate("Apartment, Suite")}
                            onChange={e => streetAddress2ChangeHandler(e.currentTarget.value)}
                            value={streetAddress2}
                            data-test-selector="createAccount_floorSuiteNumber"
                            type="address"
                            required={false}
                            {...{ formId }}
                        />
                    </GridItem>
                    <GridItem {...styles.halfWidthItem}>
                        <TextField
                            {...styles.emailTextField}
                            label={translate("City")}
                            placeholder={translate("City Name")}
                            onChange={e => cityChangeHandler(e.currentTarget.value)}
                            value={city}
                            required
                            data-test-selector="createAccount_city"
                            // error={isSubmitted && cityError}
                            type="city"
                            {...{ formId }}
                        />
                    </GridItem>
                    <GridItem {...styles.quarterWidthItem}>
                        {countries !== undefined && (
                            <Select
                                label={translate("State")}
                                required
                                placeholder="Select"
                                onChange={stateChangeHandler}
                                // error={isSubmitted && stateError}
                                value={stateObj !== undefined ? stateObj!.id : ""}
                                {...{ formId }}
                            >
                                <option value="">{translate("Select")}</option>
                                {countryObj === undefined
                                    ? countries!
                                          .find(c => c.abbreviation === countries[0].abbreviation)!
                                          .states!.map(st => (
                                              <option key={st.id} value={st.id}>
                                                  {st.name}
                                              </option>
                                          ))
                                    : countries!
                                          .find(c => c.abbreviation === countryObj.abbreviation)!
                                          .states!.map(st => (
                                              <option key={st.id} value={st.id}>
                                                  {st.name}
                                              </option>
                                          ))}
                            </Select>
                        )}
                    </GridItem>
                    <GridItem {...styles.quarterWidthItem}>
                        <TextField
                            {...styles.emailTextField}
                            label={translate("Zip")}
                            onChange={e => postalCodeChangeHandler(e.currentTarget.value)}
                            value={postalCode}
                            required
                            placeholder={translate("Zip")}
                            data-test-selector="createAccount_zipCode"
                            // error={isSubmitted && postalCodeError}
                            type="postalCodeUS"
                            {...{ formId }}
                        />
                    </GridItem>
                    <GridItem {...styles.emailGridItem}>
                        <StyledWrapper {...styles.separator}></StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.halfWidthItem}>
                        <TextField
                            {...styles.emailTextField}
                            type="email"
                            label={translate("Email Address")}
                            placeholder={translate("Email@Address.com")}
                            onChange={e => emailChangeHandler(e.currentTarget.value)}
                            value={email}
                            data-test-selector="createAccount_email"
                            maxLength={256}
                            required
                            // error={isSubmitted && emailError}
                            {...{ formId }}
                        />
                    </GridItem>
                    <GridItem {...styles.fakeItem}></GridItem>
                    <GridItem {...styles.halfWidthItem}>
                        <TippyPopup
                            maxWidth={320}
                            showCloseButton={false}
                            removeWrapper={true}
                            showOverlay={false}
                            content={
                                <>
                                    <Typography variant="h5">{translate("Password Requirements")}</Typography>
                                    <PasswordRequirementsValidator
                                        password={password}
                                        passwordMinimumLength={passwordMinimumLength}
                                        passwordRequiresDigit={passwordRequiresDigit}
                                        passwordRequiresLowercase={passwordRequiresLowercase}
                                        passwordRequiresUppercase={passwordRequiresUppercase}
                                        passwordRequiresSpecialCharacter={passwordRequiresSpecialCharacter}
                                    />
                                </>
                            }
                            offset={[0, 4]}
                            placement="bottom-start"
                            type="menu"
                            appendTo="parent"
                            trigger="click"
                            extendedStyles={styles.tippyPopup}
                            arrow={true}
                        >
                            <TextField
                                id="password"
                                {...styles.passwordTextField}
                                type={showPassword ? undefined : "password"}
                                label={translate("Password")}
                                placeholder={translate("Your Password")}
                                value={password}
                                onChange={e => passwordChangeHandler(e.currentTarget.value)}
                                // error={isSubmitted && passwordErrorMessage}
                                iconProps={{ ...styles.passwordIcon, src: showPassword ? EyeOff : Eye }}
                                iconClickableProps={{
                                    onClick: () => {
                                        setShowPassword(!showPassword);
                                    },
                                }}
                                clickableText={password}
                                data-test-selector="createAccount_password"
                                required
                                {...{ formId }}
                            />
                        </TippyPopup>
                    </GridItem>
                    <GridItem {...styles.halfWidthItem}>
                        <TextField
                            id="confirm-password"
                            {...styles.confirmPasswordTextField}
                            type={showConfirmPassword ? undefined : "password"}
                            label={translate("Confirm Password")}
                            placeholder={translate("Confirm Your Password")}
                            value={confirmPassword}
                            onChange={e => confirmPasswordChangeHandler(e.currentTarget.value)}
                            // error={isSubmitted && confirmPasswordErrorMessage}
                            iconProps={{ ...styles.confirmPasswordIcon, src: showConfirmPassword ? EyeOff : Eye }}
                            iconClickableProps={{
                                onClick: () => {
                                    setShowConfirmPassword(!showConfirmPassword);
                                },
                            }}
                            clickableText={confirmPassword}
                            data-test-selector="createAccount_confirmPassword"
                            required
                            {...{ formId }}
                        />
                    </GridItem>
                    <GridItem {...styles.emailGridItem}>
                        <StyledWrapper {...styles.separator}></StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.subscriptionGridItem}>
                        <CheckboxGroup>
                            <Checkbox
                                onChange={(e, value) => setIsSubscribed(value)}
                                checked={isSubscribed}
                                data-test-selector="createAccount_Subscription"
                            >
                                {siteMessage("SignIn_Sign_Up_Newsletter")}
                            </Checkbox>
                        </CheckboxGroup>
                    </GridItem>
                    <GridItem {...styles.subscriptionGridItem}>
                        <CheckboxGroup>
                            <Checkbox
                                onChange={(e, value) => setAgreeToTerms(value)}
                                checked={agreeToTerms}
                                data-test-selector="createAccount_Terms"
                            >
                                {siteMessage("SignIn_Sign_Up_Agree")}{" "}
                                <Link
                                    onClick={e => e.stopPropagation()}
                                    href={termsConditionsUrl}
                                    target="_blank"
                                    {...styles.link}
                                >
                                    {siteMessage("SignIn_Sign_Up_Terms")}
                                </Link>
                            </Checkbox>
                        </CheckboxGroup>
                    </GridItem>
                    {errorMessage && (
                        <GridItem {...styles.submitErrorGridItem}>
                            <Notify type="danger" showClose={false}>
                                {errorMessage}
                            </Notify>
                        </GridItem>
                    )}
                    <GridItem {...styles.reCaptchaGridItem}>
                        <ReCaptcha recaptchaRef={recaptchaRef} location="CreateAccount" />
                    </GridItem>
                    <GridItem {...styles.submitGridItem}>
                        <Button
                            {...styles.submitButton}
                            onClick={submitHandler}
                            disabled={isSubmitting || !isSubmitEnabled}
                            data-test-selector="createAccount_createButton"
                        >
                            {translate("Create Account")}
                        </Button>
                    </GridItem>
                </GridContainer>
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CreateAccount),
    definition: {
        allowedContexts: ["CreateAccountPage"],
        group: "Create Account",
        // XNG-Changes
        fieldDefinitions: [
            {
                editorTemplate: "LinkField",
                defaultValue: {
                    type: "Page",
                    value: "",
                },
                name: "termsConditions",
                displayName: "Terms & Conditions",
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
